import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  TextField,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  AppBar,
  Toolbar,
  Tooltip,
  CircularProgress,
  Stack
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ContentCopy as CopyIcon,
  Logout as LogoutIcon,
  ViewList as ViewListIcon
} from '@mui/icons-material';
import { virtualBankAccountAPI } from '../services/api';
import ThemeToggle from '../components/ThemeToggle';
import { useSyncContext } from '../contexts/SyncContext';

const AllAccounts = () => {
  const { syncTrigger } = useSyncContext();
  
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingNickname, setEditingNickname] = useState(null);
  const [nicknameValue, setNicknameValue] = useState('');

  useEffect(() => {
    fetchAccounts();
  }, []);

  // Listen for sync triggers from other components (like initial sync in Layout)
  useEffect(() => {
    if (syncTrigger > 0) {
      // Refresh accounts data when a sync trigger is received
      fetchAccounts();
    }
  }, [syncTrigger]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await virtualBankAccountAPI.getAllComprehensive();
      setAccounts(response.data.accounts);
      setError('');
    } catch (err) {
      setError('Failed to fetch virtual bank accounts');
      console.error('Error fetching accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateNickname = async (accountId) => {
    try {
      await virtualBankAccountAPI.updateNickname(accountId, nicknameValue);
      setSuccess('Nickname updated successfully!');
      setEditingNickname(null);
      setNicknameValue('');
      await fetchAccounts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update nickname');
    }
  };

  const startEditingNickname = (accountId, currentNickname) => {
    setEditingNickname(accountId);
    setNicknameValue(currentNickname || '');
  };

  const cancelEditingNickname = () => {
    setEditingNickname(null);
    setNicknameValue('');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard!');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  const closeSnackbar = () => {
    setError('');
    setSuccess('');
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'background.default'
    }}>
      {/* App Bar */}
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            All Accounts
          </Typography>
          <Button
            color="inherit"
            startIcon={<ViewListIcon />}
            onClick={() => window.location.href = '/virtual-bank-accounts'}
            sx={{ mr: 2 }}
          >
            Virtual Bank Accounts
          </Button>
          <ThemeToggle />
          <Button
            color="inherit"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{ ml: 3 }}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        p: 2,
        backgroundColor: 'background.default',
        overflow: 'hidden'
      }}>

        {/* Main Table */}
        <Paper sx={{
          width: '100%',
          overflow: 'hidden',
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <TableContainer sx={{
            flex: 1,
            overflow: 'auto'
          }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Customer ID
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Account Holder
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Account Number
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Routing Number
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Bank Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Balance
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Nickname
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Update Nickname
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Copy Details
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Total Paid
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && accounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        Loading virtual bank accounts...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : accounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No virtual bank accounts found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  accounts.map((account) => (
                    <TableRow key={account._id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {account.stripeCustomerId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {account.accountHolderName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {account.accountNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {account.routingNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {account.bankName || 'Stripe Bank'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`$${(account.balance || 0).toFixed(2)}`}
                          color={account.balance > 0 ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {editingNickname === account._id ? (
                          <TextField
                            size="small"
                            value={nicknameValue}
                            onChange={(e) => setNicknameValue(e.target.value)}
                            placeholder="Enter nickname"
                            sx={{ minWidth: 150 }}
                          />
                        ) : (
                          <Typography variant="body2">
                            {account.nickname || 'No nickname'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingNickname === account._id ? (
                          <Stack direction="row" spacing={1}>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleUpdateNickname(account._id)}
                            >
                              <SaveIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={cancelEditingNickname}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Stack>
                        ) : (
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => startEditingNickname(account._id, account.nickname)}
                          >
                            Update Nickname
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Copy account details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => copyToClipboard(
                              `Account: ${account.accountNumber}, Routing: ${account.routingNumber}, Holder: ${account.accountHolderName}, Bank: ${account.bankName || 'Stripe Bank'}`
                            )}
                          >
                            <CopyIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`$${(account.totalPaid || 0).toFixed(2)}`}
                          variant="outlined"
                          color="info"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!error || !!success}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={error ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {error || success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AllAccounts;